import React, { useState, useEffect, useCallback, useContext, useRef } from 'react';

import EmailLanguageSelector from '../../../components/common/EmailLanguageSelector';
import GiftCardAmount from './GiftCardAmount';
import NumberInput from '../../../components/widgets/NumberInput';
import Config from '../../../Config';
import { useCartContext } from '../../../context/CartContext';
import Modal from '../../../components/common/Modal';
import { useTranslation } from 'react-i18next';
import { formatDateTime } from '../../../utils/DateUtils';
import DateTimePicker from '../../../components/common/DateTimePicker';
import moment from 'moment';
import { useGlobalContext } from '../../../context/GlobalContext';
import { CartContext } from '../../../context/CartContext';
import validator from 'validator';
import { cartAmountLimitAcheived, cartLimitAcheived } from '../../../utils/cartUtils';
import InfoContent from '../../../components/common/InfoContent';
import Button from '../../../components/common/Button';
import MessageModal from '../../../components/common/MessageModal';
import SendDateModal from '../../../components/common/SendDateModal';
import AccessibleContent from '../../../utils/AccessibleContent';
import { isAmountValid } from '../../../utils/utils';

const TimeListbox = ({ id, value, onChange, label }) => {
  const [open, setOpen] = useState(false);
  const boxRef = useRef(null);
  const listRef = useRef(null);
  const hours = Array.from({ length: 24 }, (_, h) => `${String(h).padStart(2, '0')}:00`);

  useEffect(() => {
    if (!open) return;
    const handleClickOutside = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  useEffect(() => {
    if (open && listRef.current) {
      const sel = listRef.current.querySelector('[aria-selected="true"]');
      if (sel) sel.scrollIntoView({ block: 'nearest' });
    }
  }, [open]);

  const move = (delta) => {
    const idx = hours.indexOf(value);
    const next = Math.min(hours.length - 1, Math.max(0, (idx === -1 ? 0 : idx) + delta));
    if (hours[next] !== value) onChange(hours[next]);
  };

  return (
    <div className="timeListbox" ref={boxRef}>
      <button
        type="button"
        id={id}
        className="dateModalTimeSelect timeListboxToggle"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={label}
        onClick={() => setOpen(!open)}
        onKeyDown={(e) => {
          if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
            e.preventDefault();
            if (!open) setOpen(true);
            else move(e.key === 'ArrowDown' ? 1 : -1);
          } else if (e.key === 'Escape' && open) {
            e.stopPropagation();
            setOpen(false);
          }
        }}
      >
        {`${parseInt(value, 10)}h`}
      </button>
      {open && (
        <ul className="timeListboxList" role="listbox" ref={listRef} aria-label={label}>
          {hours.map((v) => (
            <li
              key={v}
              role="option"
              aria-selected={v === value}
              onClick={() => {
                onChange(v);
                setOpen(false);
              }}
            >
              {`${parseInt(v, 10)}h`}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

const Step1 = ({ step, store, selectedStore, lang, onStepChange, cart, selectedStoreCards }) => {
  const { t } = useTranslation();
  const { formatAmounts } = useGlobalContext();
  const handleLanguageChange = useCallback((selectedLang) => {
    setCardData(prev => ({
      ...prev,
      cardLanguage: store.languages.find(l => l.code === selectedLang.code),
      cardLanguageImg: `assets/images/flags/${selectedLang.code}.png`
    }));
  }, [store.languages]);
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState(""); // 'cart', 'delete', 'preview'
  const [itemToDelete] = useState(null);
  const [previewUrl, setPreviewUrl] = useState("");
  const [msgModalOpen, setMsgModalOpen] = useState(false);
  const [msgChoice, setMsgChoice] = useState(null);
  const [dateModalOpen, setDateModalOpen] = useState(false);
  const dateSnapshotRef = useRef(null);
  const [addedFeedback, setAddedFeedback] = useState("");
  const [isAdding, setIsAdding] = useState(false);
  const [msgBlockHidden, setMsgBlockHidden] = useState(false);
  const [destTouched, setDestTouched] = useState(false);


  const isProSignboard = store.isProSignboard;
  const isAmexAvailable = store.allowAmex;
  const orderCardByAmountInCart = store.orderByAmountInCart ? store.orderByAmountInCart : false;
  const nbAddressLine = store.nbAddressLine || 3;
  const filteredCards = store.cards.filter(
    (card) => card.store === selectedStore || card.store === "both"
  );
  const {
    totalAmounts,
    cartItemsCountMax,
    cartAmountMax,
    addCard,
    deleteRequestCartItem
  } = useContext(CartContext);
  const professionalUser = store.professionalUser;

  const cardMessageEnabled = store.cardMessageEnabled !== false; 
  const displayDemoButton = store.displayDemoButton !== false;
  const canHandDeliver = store.canHandDeliver !== false; 
  const showPersonalsMsgs = store.personalsMsgs !== false;

  const [cardData, setCardData] = useState(() => {
    if (professionalUser) {
      return {
        selectedCard: null,

        desemail: professionalUser.email,
        desconfEmail: professionalUser.email,
        isSelfDestination: professionalUser.discount ? true : false,
        isEmailDestination: professionalUser.discount ? false : true
      };
    }
    return { selectedCard: null };
  });
  const [selectedMessageIndex, setSelectedMessageIndex] = useState("");
  const [dateTimeMode, setDateTimeMode] = useState("now");
  const [quantity, setQuantity] = useState(1);
  const [useAmex, setUseAmex] = useState(false);
  const [infoField, setInfoField] = useState("");
  const [errors, setErrors] = useState({});

  const handleShowInfo = (field) => setInfoField(field);
  const handleHideInfo = () => setInfoField("");

  const openDateModal = () => {
    dateSnapshotRef.current = {
      mode: dateTimeMode,
      fields: {
        date: cardData.date,
        time: cardData.time,
        datepicker: cardData.datepicker,
        timepicker: cardData.timepicker,
        sendDate: cardData.sendDate,
        sendDateTmp: cardData.sendDateTmp
      }
    };
    if (dateTimeMode !== 'after') {
      setDateTimeMode('after');
      if (!cardData.datepicker) {
        const nextDateTime = formatDateTime.syncDateTime('after', store);
        setCardData(prev => ({ ...prev, ...nextDateTime }));
      }
    }
    setDateModalOpen(true);
  };

  const cancelDateModal = () => {
    const snap = dateSnapshotRef.current;
    if (snap) {
      setDateTimeMode(snap.mode);
      setCardData(prev => ({ ...prev, ...snap.fields }));
    }
    setDateModalOpen(false);
  };

  const chooseImmediateSend = () => {
    setDateTimeMode('now');
    const currentDateTime = formatDateTime.syncDateTime('now', store);
    setCardData(prev => ({ ...prev, ...currentDateTime }));
    setDateModalOpen(false);
  };
  const [isFormValid, setIsFormValid] = useState(false);
  const filteredMessages =
    store.personalsMsgs && Array.isArray(store.personalsMsgs) && store.personalsMsgs.filter(
      msg => msg.store === selectedStore || msg.store === 'both'
    );
  const checkCartLimit = () => {
    return cartLimitAcheived({
      store,
      selectedStore,
      cart,
      cartItemsCountMax,
      qtyToAdd: quantity
    });
  };

  const checkAmountLimit = () => {
    const cardMontant = cardData.customMontant !== undefined ?
      cardData.customMontant :
      cardData.montant;

    return cartAmountLimitAcheived({
      store,
      totalAmounts,
      cardMontant,
      qty: quantity
    });
  };

  const handleMessageChange = (e) => {

    const index = e.target.value;
    setSelectedMessageIndex(index);
    if (index !== "") {
      const msg = filteredMessages[index];
      setCardData(prev => ({ ...prev, customMsg: msg.texts[lang] }));
    } else {
      setCardData(prev => ({ ...prev, customMsg: "" }));
    }
  };



  const [selectedCardId, setSelectedCardId] = useState(null);
  const [destinationType, setDestinationType] = useState(() => {
    if (professionalUser && professionalUser.discount) {
      return "main";
    }
    return "email";
  });
  const checkProfessionalUserLimits = () => {
    if (!professionalUser || !professionalUser.discount) {
      return { valid: true, error: null };
    }

    const cardMontant = cardData.customMontant !== undefined ?
      cardData.customMontant :
      cardData.montant;

    const currentTotal = totalAmounts || 0;
    const currentCardCount = cart.items 
      ? cart.items.reduce((total, item) => total + (item.qty || 1), 0)
      : 0;
    const newTotal = currentTotal + (Number(cardMontant) * quantity);
    const newCardCount = currentCardCount + quantity;

    if (professionalUser.limit_amount && professionalUser.limit_amount > 0) {
      if (newTotal > professionalUser.limit_amount) {
        return {
          valid: false,
          error: t("Limite de montant professionnel dépassée") + ` (${professionalUser.limit_amount})`
        };
      }
    }

    if (professionalUser.limit_card && professionalUser.limit_card > 0) {
      if (newCardCount > professionalUser.limit_card) {
        return {
          valid: false,
          error: t("Limite de cartes professionnelles dépassée") + ` (${professionalUser.limit_card})`
        };
      }
    }

    return { valid: true, error: null };
  };

  useEffect(() => {
    if (professionalUser && professionalUser.discount) {
      setDestinationType("main");
      setCardData(prev => ({
        ...prev,
        isSelfDestination: true,
        isEmailDestination: false,
        desemail: professionalUser.email,
        desconfEmail: professionalUser.email
      }));
    }
  }, [professionalUser]);
  useEffect(() => {
    setCardData(prev => ({ ...prev, isSelfDestination: destinationType === "main" }));
  }, [destinationType]);
  useEffect(() => {
    setCardData(prev => {
      if (!prev.isForSelf) return prev;
      return {
        ...prev,
        desfirstname: prev.senderfirstname || '',
        deslastname: prev.senderlastname || '',
        desemail: prev.senderemail || '',
        desconfEmail: prev.senderemail || ''
      };
    });
  }, [cardData.isForSelf, cardData.senderfirstname, cardData.senderlastname, cardData.senderemail]);
  useEffect(() => {
    checkFormValidity();
  }, [cardData, destinationType]);
  useEffect(() => {
    if (filteredCards.length > 0 && (!cardData.selectedCard)) {
      setSelectedCardId(filteredCards[0]?.id)
      setCardData(prev => ({
        ...prev,
        selectedCard: filteredCards[0]
      }));
    }
  }, [selectedStore]);
  useEffect(() => {
    const check = () => {
      const el = document.getElementById('row_customMsg');
      setMsgBlockHidden(!!el && el.offsetParent === null);
    };
    check();
    window.addEventListener('load', check);
    return () => window.removeEventListener('load', check);
  }, [cardData.montant]);
  if (step !== 1) return null;
  const emailLangChosen = !(store.languages && store.languages.length > 1) || !!cardData.cardLanguage;
  const hasAmount = Number(cardData.montant) > 0;
  const msgBlockShown = !isProSignboard && cardMessageEnabled;
  const msgStepDone = !msgBlockShown || msgBlockHidden || msgChoice !== null || !!cardData.customMsg;
  const modeBlockShown = !isProSignboard && selectedStore === 'digital' && canHandDeliver;
  const modeStepDone = !modeBlockShown || destTouched;
  const rawAccroche = selectedStore === 'digital'
    ? (store.texteAccroche?.[lang] || '')
    : (store.physical_store?.texts?.card_list_teaser?.[lang] || '');
  const accrocheHtml = rawAccroche.replace("<p>", "<h2>").replace("</p>", "</h2>");
  const hasAccroche = accrocheHtml.replace(/<[^>]*>/g, '').replace(/&nbsp;/gi, ' ').trim().length > 0;
  const basicEmailRegex = /^[^\s<>()[\]\\,;:"']+@[^\s<>()[\]\\,;:"']+\.[^\s<>()[\]\\,;:"']+$/;
  const checkFormValidity = () => {
    let valid =
      cardData.montant && cardData.montant > 0 &&
      cardData.desfirstname && cardData.desfirstname.trim() !== "" &&
      cardData.deslastname && cardData.deslastname.trim() !== "";

    if (!isProSignboard) {
      const senderEmail = cardData.senderemail?.trim();
      valid = valid &&
        cardData.senderfirstname && cardData.senderfirstname.trim() !== "" &&
        cardData.senderlastname && cardData.senderlastname.trim() !== "" &&
        senderEmail && basicEmailRegex.test(senderEmail) && validator.isEmail(senderEmail);
    }

      if (destinationType === "email") {
        const email = cardData.desemail?.trim();
        const confirmEmail = cardData.desconfEmail?.trim();
        const basicEmailRegex = /^[^\s<>()[\]\\,;:"']+@[^\s<>()[\]\\,;:"']+\.[^\s<>()[\]\\,;:"']+$/;
        const emailValid = email && basicEmailRegex.test(email) && validator.isEmail(email);
        valid = valid && emailValid && confirmEmail === email;
      }
  
    if (selectedStore === 'physical') {
      valid = valid && 
        cardData.des_address_1 && 
        cardData.des_city &&
        cardData.des_zip && 
        cardData.des_country;
    }
  
    setIsFormValid(valid);
    return valid;
  };
  const validateForm = (isSubmitting = false) => {
    const newErrors = {};
    if (!isAmountValid(cardData.montant, store.offers)) {
      newErrors.montant = t("Le montant sélectionné n'est pas ou plus valide.");
    }
    if (checkCartLimit()) {
      newErrors.cartLimit = t("Vous avez atteint la limite maximale d'articles dans votre panier.");
    }

    if (checkAmountLimit()) {
      newErrors.cartAmountLimit = t("Vous avez atteint la limite maximale de montant pour votre panier.");
    }
    const professionalLimitsCheck = checkProfessionalUserLimits();
    if (!professionalLimitsCheck.valid) {
      newErrors.professionalLimit = professionalLimitsCheck.error;
    }
    if (isSubmitting) {
      const isProWithDiscount = professionalUser && professionalUser.discount;
      const skipEmailValidation = isProWithDiscount || destinationType === "main";


      if (!isProSignboard) {
        if (!cardData.senderfirstname || cardData.senderfirstname.trim() === "") {
          newErrors.senderfirstname = t("Veuillez saisir votre prénom.");
        }
        if (!cardData.senderlastname || cardData.senderlastname.trim() === "") {
          newErrors.senderlastname = t("Veuillez saisir votre nom.");
        }
        const senderEmail = cardData.senderemail?.trim();
        if (!senderEmail || !basicEmailRegex.test(senderEmail) || !validator.isEmail(senderEmail)) {
          newErrors.senderemail = t("Veuillez saisir un email valide.");
        }
      }
      if (!cardData.desfirstname || cardData.desfirstname.trim() === "") {
        newErrors.desfirstname = t("Veuillez saisir votre prénom.");
      }
      if (!cardData.deslastname || cardData.deslastname.trim() === "") {
        newErrors.deslastname = t("Veuillez saisir votre nom.");
      }
      if (!skipEmailValidation && destinationType === "email") {
        const email = cardData.desemail?.trim();
        const confirmEmail = cardData.desconfEmail?.trim();
        const basicEmailRegex = /^[^\s<>()[\]\\,;:"']+@[^\s<>()[\]\\,;:"']+\.[^\s<>()[\]\\,;:"']+$/;
      
        if (!email) {
          newErrors.desemail = t("Veuillez saisir un email valide.");
        } else if (!basicEmailRegex.test(email) || !validator.isEmail(email)) {
          newErrors.desemail = t("Veuillez saisir un email valide.");
        }
      
        if (email !== confirmEmail) {
          newErrors.desconfEmail = t("Les adresses email ne correspondent pas.");
        }
      }
      if (cardData.customMsg && cardData.customMsg.length > (store.physical_store?.message_length_limit || Config.app.max_msg_length)) {
        newErrors.customMsg = t("Votre message est trop long")
      }
    }

    if (isSubmitting) {
      setErrors(newErrors);
    }
    return Object.keys(newErrors).length === 0;
  };
  const handleSubmit = (e) => {
    e.preventDefault();
    if (store.isProSignboard || validateForm(true)) {
      const now = new Date();
      const currentDate = now.toISOString().split('T')[0];
      const currentTime = now.toTimeString().split(' ')[0];



      for (let i = 0; i < quantity; i++) {
        const newCard = {
          card: cardData.selectedCard?.src,
          signboardId: store.id,
          visualId: cardData.selectedCard?.id,
          amount: cardData.montant,
          message: cardData.customMsg,
          emailReceiver: professionalUser && professionalUser.discount
            ? professionalUser.email
            : cardData.desemail,
          firstNameReceiver: cardData.desfirstname,
          lastNameReceiver: cardData.deslastname,
          isSelfDestination: destinationType === 'main',

          sendDate: dateTimeMode === 'after'
            ? `${cardData.date} ${cardData.time}`
            : `${currentDate} ${currentTime}`,


          sendDateTmp: dateTimeMode === 'after'
            ? new Date(`${cardData.date} ${cardData.time}`).getTime() / 1000
            : Math.floor(now.getTime() / 1000),
            
          cgv: true,
          receiveInfos: "0",
          qty: 1,
          discount: cardData.cardData,
          momentSend: dateTimeMode,
          cardLanguage: cardData.cardLanguage?.code || 'fr',
          isGettingInvoiceActivated: store.sending_invoice_activated === 2,
          isGettingLivstickVideo: false,
          firstNameSender: cardData.senderfirstname || '',
          lastNameSender: cardData.senderlastname || '',
          emailSender: cardData.senderemail || '',
        };
        if (selectedStore === 'physical') {
          newCard.physical = {
            address_1: cardData.des_address_1,
            address_2: cardData.des_address_2,
            address_3: cardData.des_address_3,
            address_4: cardData.des_address_4,
            city: cardData.des_city,
            zip: cardData.des_zip,
            country: typeof cardData.des_country === 'string'
              ? cardData.des_country
              : cardData.des_country[lang],
            state: cardData.des_state
              ? getStateName(cardData.des_state)
              : null,
            state_code: cardData.des_state
              ? cardData.des_state.code
              : null
          };
        }
        addCard(newCard);
      }
      setAddedFeedback(t("La carte a été ajoutée à votre panier"));
      setIsAdding(true);
      window.setTimeout(() => {
        handleAutreCarteModal();
        setMsgChoice(null);
        setIsAdding(false);
        window.scrollTo({ top: 0, behavior: 'instant' });
        window.dispatchEvent(new CustomEvent('rf-open-cart-drawer'));
      }, 600);
    }
  };
  const handleAutreCarteModal = () => {
    setShowModal(false);
    setSelectedCardId(filteredCards[0]?.id)
    setCardData(prev => ({
      selectedCard: filteredCards[0],
      senderfirstname: prev.senderfirstname,
      senderlastname: prev.senderlastname,
      senderemail: prev.senderemail
    }));
    setDestinationType("email");
    setDestTouched(false);
    setSelectedMessageIndex("");
    setDateTimeMode("now");
  };
  const getStateName = (state) => {
    if (!state?.names) return '';
    return (
      state.names[lang] ||
      state.names[store.default_language] ||
      state.names[Object.keys(state.names)[0]]
    ) + ` (${state.code})`;
  };

  const handleValiderAchat = () => {
    setShowModal(false);
    onStepChange(2);
  };

  const showModalDemo = () => {
    setModalType('preview');
  
    const receiver_type = destinationType === "email" ? 0 : 1;
  
    const first_name = encodeURIComponent(cardData.desfirstname || "");
    const last_name = encodeURIComponent(cardData.deslastname || "");
    const message = encodeURIComponent(cardData.customMsg || "");
    const email = encodeURIComponent(cardData.desemail || "");
  
    const templateUrl =
      `${store.template[lang]}` +
      `&receiver_type=${receiver_type}` +
      `&first_name=${first_name}` +
      `&last_name=${last_name}` +
      `&message=${message}` +
      `&email=${email}`;
  
    console.log("templateUrl", templateUrl);
  
    setPreviewUrl(templateUrl);
    setShowModal(true);
  };
  const handleSelectAmount = (amount) => {
    setCardData(prev => ({ ...prev, montant: Number(amount) }));
  };

  const FieldError = ({ error, id }) => {
    if (!error) return null;

    return (
      <div className="field-error" id={`error-${id}`} role="alert">
        <span className="error-icon" aria-hidden="true">⚠️</span>
        <span className="error-message">{error}</span>
      </div>
    );
  };

  const senderBlock = !isProSignboard && (
    <div className="senderSection">
      <h2 className="rfTitle" style={{ padding: 0, textAlign: 'left', marginTop: 32 }}>
        {t("C'est de la part de qui ?")}
      </h2>
      <div className="fieldsGroup">
        <div className="formField" id="row_senderfirstname">
          <div className="formControl">
            <input
              type="text"
              name="senderfirstname"
              id="senderfirstname"
              placeholder={t("Prénom")}
              aria-label={t("Prénom")}
              required
              aria-required="true"
              value={cardData.senderfirstname || ""}
              onChange={(e) => setCardData(prev => ({ ...prev, senderfirstname: e.target.value }))}
              className={errors.senderfirstname ? 'error' : ''}
              aria-invalid={errors.senderfirstname ? 'true' : 'false'}
              aria-describedby={errors.senderfirstname ? 'error-senderfirstname' : undefined}
            />
            <label className="flLabel" htmlFor="senderfirstname" aria-hidden="true">{t("Prénom")}</label>
          </div>
          {errors.senderfirstname && <FieldError error={errors.senderfirstname} id="senderfirstname" />}
        </div>
        <div className="formField" id="row_senderlastname">
          <div className="formControl">
            <input
              type="text"
              name="senderlastname"
              id="senderlastname"
              placeholder={t("Nom")}
              aria-label={t("Nom")}
              required
              aria-required="true"
              value={cardData.senderlastname || ""}
              onChange={(e) => setCardData(prev => ({ ...prev, senderlastname: e.target.value }))}
              className={errors.senderlastname ? 'error' : ''}
              aria-invalid={errors.senderlastname ? 'true' : 'false'}
              aria-describedby={errors.senderlastname ? 'error-senderlastname' : undefined}
            />
            <label className="flLabel" htmlFor="senderlastname" aria-hidden="true">{t("Nom")}</label>
          </div>
          {errors.senderlastname && <FieldError error={errors.senderlastname} id="senderlastname" />}
        </div>
        <div className="formField" id="row_senderemail">
          <div className="formControl">
            <input
              type="email"
              name="senderemail"
              id="senderemail"
              placeholder={t("Adresse email de l'expéditeur")}
              aria-label={t("Adresse email de l'expéditeur")}
              required
              aria-required="true"
              value={cardData.senderemail || ""}
              onChange={(e) => setCardData(prev => ({ ...prev, senderemail: e.target.value }))}
              className={errors.senderemail ? 'error' : ''}
              aria-invalid={errors.senderemail ? 'true' : 'false'}
              aria-describedby={errors.senderemail ? 'error-senderemail' : undefined}
            />
            <label className="flLabel" htmlFor="senderemail" aria-hidden="true">{t("Adresse email de l'expéditeur")}</label>
          </div>
          {errors.senderemail && <FieldError error={errors.senderemail} id="senderemail" />}
        </div>
      </div>
    </div>
  );

  return (
    <div>
      <form
        method="post"
        name="step1Form"
        className={`step1Form ${store.offers && store.offers[4] && store.offers[4].type === 'manual' ? 'haveManualAmount' : ''}`}
        onSubmit={handleSubmit}
        noValidate
      >

        {hasAccroche && (
          <AccessibleContent
            className="rfAccroche accrocheColor"
            htmlContent={accrocheHtml}
          />
        )}

        {/* Partie Carte */}
        <section aria-labelledby="card-section-title">
          <div className="cardPart">
            <div className="cardSection">
              <div className="cardPreview">
                <h2 id="card-section-title" className="sr-only">
                  {t("Section de sélection de carte cadeau")}
                </h2>
                {selectedStoreCards.length > 0 && (
                  <img
                    src={cardData.selectedCard ? cardData.selectedCard.src : selectedStoreCards[0].src}
                    data-id={cardData.selectedCard ? cardData.selectedCard.id : selectedStoreCards[0].id}
                    alt={t("Aperçu de la carte cadeau")}
                    fetchPriority="high"
                    loading="eager"
                    onError={(e) => { e.target.onerror = null; e.target.src = 'fallback-image.webp' }}
                  />
                )}
              </div>
            </div>
            <div className="cardInfoSection">
              <div className="cardInfoBox">
                <div className="topBox cardInfos" >
                  <h2 className="rfTitle rfCardLabel">{t('Choisissez votre modèle')}</h2>
                </div>
                {filteredCards.length > 1 && (
                <div className="cardThumbnails">
                  {filteredCards.map((card) => {
                    const isSelected = card.id === selectedCardId;
                    return (

                      <div key={`card-container-${card.id}`}>
                        <button
                          style={{ borderWidth: 0 }}
                          type="button"
                          className={`cardThumbnailButton ${isSelected ? 'selected mainColor' : ''}`}
                          onClick={() => {
                            setSelectedCardId(card.id);
                            setCardData(prev => ({ ...prev, selectedCard: card }));
                          }}
                          aria-pressed={isSelected}
                          aria-label={`Sélectionner la carte ${card.name || card.id}`}
                        >
                          <img
                            src={card.src}
                            alt={card.name || `Carte ${card.id}`}
                            className={`cardThumbnail ${isSelected ? 'selected mainColor' : ''}`}
                          />
                        </button>
                      </div>
                    );
                  })}
                </div>
                )}
              </div>

              {selectedStore === 'physical' && cart.items && cart.items.length === 0 && (
                <div className="btnsArea alignCenter">
                  <a href="/home/digital" className="rfBtn buyOther">
                    {t('Envoyer une carte digitale')}
                  </a>
                </div>
              )}


              {professionalUser && professionalUser.discount && (
                <div className="rfDiscount" style={{
                  padding: '10px',
                  backgroundColor: '#f0f8ff',
                  border: '1px solid #007bff',
                  borderRadius: '4px',
                  margin: '10px 0'
                }}>
                  {t("Votre remise de")} {professionalUser.discount}% {t("sera appliquée sur votre panier")}
                </div>
              )}


              {selectedStore === 'digital' && displayDemoButton && (
                <div className='bottomSection'>
                  <div className="btnsArea alignCenter">
                    <button
                      type="button"
                      className="rfBtn buyOther"
                      onClick={showModalDemo}
                      aria-label={t('Carte cadeau de démonstration')}
                    >
                      {t('Carte cadeau de démonstration')}
                    </button>
                  </div>
                </div>
              )}

            </div>
          </div>
        </section>
        <section aria-labelledby="form-section-title">
          <h2 id="form-section-title" className="sr-only">{t("Formulaire de commande")}</h2>
          {/* Partie Formulaire */}
          <div className="mainCol">
            <div className="whiteBox">
              <div className="stepBox">
                <div className={`formBox${hasAmount && !msgStepDone ? " rf-gate-msg" : ""}`} >
                  <GiftCardAmount
                    offers={store.offers}
                    extrasOffers={store.extra_offers}
                    selectedStore={selectedStore}
                    onSelectAmount={handleSelectAmount}
                    selectedAmount={cardData.montant}
                    store={{ ...store, useAmex }}
                  />

                  {hasAmount && isAmexAvailable && (
                    <div className="cardAmexSection customCheckbox">
                      <div className="amexCheckbox">
                        <input
                          type="checkbox"
                          name="payment-amex"
                          id="paymentAmex"
                          onChange={(e) => setUseAmex(e.target.checked)}
                          aria-describedby={isAmexAvailable ? "amex-payment-info" : undefined}
                        />
                        <label htmlFor="paymentAmex">
                          <span>{t("Je désire régler mon achat avec ma carte American Express")}</span>
                          <img src="../../../assets/images/amex.jpg" className="amexLogo" alt={t("Logo American Express")} />
                        </label>
                      </div>
                      {cart.haveAmexCard && (
                        <InfoContent
                          id="forAmexPayment"
                          infoField={infoField}
                          showInfo={handleShowInfo}
                          hideInfo={handleHideInfo}
                          infoText={t("Une carte cadeau d’un montant réservé AMEX est présente dans le panier")}
                          context="step1_checkbox"
                          showButton={false}
                          ariaLabelShow={t("Afficher les informations sur le paiement AMEX")}
                          ariaLabelHide={t("Fermer les informations sur le paiement AMEX")}
                        />
                      )}
                    </div>
                  )}

                  {hasAmount && showPersonalsMsgs && store.personalsMsgs && Array.isArray(store.personalsMsgs) && store.personalsMsgs.length > 0 && (
                    <div className="formField" id="row_personalsMsgs">
                      <div className="formControl blockOptions">
                        <div className="optionItem">
                          <select
                            name="personalsMsgs"
                            id="personalsMsgs"
                            value={selectedMessageIndex}
                            onChange={handleMessageChange}
                            aria-label={t("Message prédéfini")}
                          >
                            <option value="">{t("Message prédéfini")}</option>
                            {filteredMessages &&
                              filteredMessages.map((msg, index) => (
                                <option key={index} value={index}>
                                  {msg.texts[lang]}
                                </option>
                              ))}
                          </select>
                        </div>
                      </div>
                    </div>
                  )}

                  {hasAmount && !isProSignboard && cardMessageEnabled && (
                    <div className="formField" id="row_customMsg">
                      <div className="formControl msgControl">
                        <h2 className="rfTitle" style={{ padding: 0 }}>
                          {cardData.customMsg ? t("Votre message personnalisé :") : t("Souhaitez-vous ajouter un message personnalisé ?")}
                        </h2>

                        {cardData.customMsg ? (
                          <>
                            <button
                              type="button"
                              className="msgPreviewCard"
                              onClick={() => setMsgModalOpen(true)}
                            >
                              <span className="msgPreviewText">{cardData.customMsg}</span>
                            </button>
                            <button
                              type="button"
                              className="msgEditLink"
                              onClick={() => setMsgModalOpen(true)}
                            >
                              {t("Modifier")}
                            </button>
                          </>
                        ) : (
                          <div className="msgChoiceCards" role="group" aria-label={t("Message personnalisé")}>
                            <button
                              type="button"
                              className={`choiceCard${msgChoice === 'add' ? ' selected' : ''}`}
                              onClick={() => {
                                setMsgChoice('add');
                                setMsgModalOpen(true);
                              }}
                            >
                              {t("Ajouter un message")}
                            </button>
                            <button
                              type="button"
                              className={`choiceCard${msgChoice === 'none' ? ' selected' : ''}`}
                              onClick={() => setMsgChoice('none')}
                            >
                              {t("Pas de message")}
                            </button>
                          </div>
                        )}

                        <MessageModal
                          isOpen={msgModalOpen}
                          initialValue={cardData.customMsg || ""}
                          maxLength={store.physical_store?.message_length_limit || Config.app.max_msg_length}
                          onSave={(val) => {
                            setCardData(prev => ({ ...prev, customMsg: val }));
                            setMsgChoice('add');
                            setMsgModalOpen(false);
                          }}
                          onDelete={() => {
                            setCardData(prev => ({ ...prev, customMsg: "" }));
                            setMsgChoice(null);
                            setMsgModalOpen(false);
                          }}
                          onClose={() => {
                            setMsgModalOpen(false);
                            if (!cardData.customMsg) setMsgChoice(null);
                          }}
                        />
                      </div>
                    </div>
                  )}

                  {/* Titre de section - Conditionné par canHandDeliver */}
                  {hasAmount && canHandDeliver && (
                    <div className="formField">
                      <div className="formControl countInputContainer">
                        {!isProSignboard && selectedStore === 'digital' && <h2 className="rfTitle" style={{ padding: 0 }}>{t("Comment souhaitez-vous offrir la e-carte cadeau ?")}</h2>}
                        {!isProSignboard && selectedStore === 'physical' && <h2 className="rfTitle" style={{ padding: 0 }}>{t("J'offre cette carte physique")}</h2>}
                        {isProSignboard && (
                          <label>
                            {t("Je commande une carte : je la recevrais alors à l'adresse email fournie au service B2B.")}
                          </label>
                        )}
                      </div>
                    </div>
                  )}

                  {hasAmount && !isProSignboard && selectedStore === 'digital' && canHandDeliver && (
                    <>
                      <div className={`formControl blockOptions customCheckbox hasInfo${destTouched ? "" : " rf-untouched"}`}>
                        <div className="optionItem checkboxRadio">
                          <input
                            type="checkbox"
                            disabled={professionalUser && professionalUser.discount}
                            name="selfDestination"
                            id="selfDestination"
                            checked={destinationType === 'main'}
                            onChange={() => { setDestTouched(true); setDestinationType('main'); }}
                            aria-label={t("Imprimer et offrir")}
                          />
                          <label style={{ paddingTop: 4 }} htmlFor="selfDestination" className="square">{t("Imprimer et offrir")}</label>
                        </div>
                        <InfoContent
                          id="forEmailDestination"
                          infoField={infoField}
                          showInfo={handleShowInfo}
                          hideInfo={handleHideInfo}
                          infoText={t("Si vous souhaitez offrir la carte cadeau uniquement en main propre après l'avoir imprimée, veuillez sélectionner ce choix.")}
                          context="step1_checkbox"
                          ariaLabelShow={t("Afficher les informations sur la remise en main propre")}
                          ariaLabelHide={t("Fermer les informations sur la remise en main propre")}
                        />
                      </div>
                      <div className={`formControl blockOptions customCheckbox hasInfo${destTouched ? "" : " rf-untouched"}`} role="group" aria-label={t("Mode d'envoi de la carte")}>
                        <div className="optionItem checkboxRadio">
                          <input
                            type="checkbox"
                            disabled={professionalUser && professionalUser.discount}
                            name="emailDestination"
                            id="emailDestination"
                            checked={destinationType === 'email'}
                            onChange={() => { setDestTouched(true); setDestinationType('email'); }}
                            aria-label={t("Envoyer par email")}
                          />
                          <label style={{ paddingTop: 4 }} htmlFor="emailDestination" className="square">{t("Envoyer par email")}</label>
                        </div>
                        <InfoContent
                          id="forSelfDestination"
                          infoField={infoField}
                          showInfo={handleShowInfo}
                          hideInfo={handleHideInfo}
                          infoText={t("Si vous souhaitez envoyer directement la carte cadeau par email au bénéficiaire, veuillez sélectionner ce choix.")}
                          context="step1_checkbox"
                          ariaLabelShow={t("Afficher les informations sur l'envoi par email")}
                          ariaLabelHide={t("Fermer les informations sur l'envoi par email")}
                        />
                      </div>
                    </>
                  )}

                  {hasAmount && modeStepDone && !isProSignboard && !cardData.isSelfDestination && store.video_enabled && (
                    <div className="formControl blockOptions customCheckbox hasInfo">
                      <div className="optionItem checkboxRadio" >
                        <input type="checkbox" name="videoEnabled" id="videoEnabled" />
                        <label htmlFor="videoEnabled" >{t("Ajouter une vidéo à votre carte")}</label>
                      </div>
                      <InfoContent
                        id="forVideo"
                        infoField={infoField}
                        showInfo={handleShowInfo}
                        hideInfo={handleHideInfo}
                        infoText={t("Sélectionnez ce choix pour joindre une vidéo à votre carte cadeau. Le lien et instructions vous seront fournis par mail après votre achat")}
                        context="step1_checkbox"
                        ariaLabelShow={t("Afficher les informations sur l'ajout d'une vidéo")}
                        ariaLabelHide={t("Fermer les informations sur l'ajout d'une vidéo")}
                      />
                    </div>
                  )}

                  {hasAmount && modeStepDone && selectedStore === 'digital' && !isProSignboard && store.sendLaterEnabled && (
                    <div className="formField">
                      <h2 className="rfTitle" id="sending_date" style={{ padding: 0 }}>
                        {t("À quel moment souhaitez-vous envoyer l'email contenant la e-carte cadeau au destinataire ?")}
                      </h2>

                      <div className="formControl dateTimeLine" aria-labelledby="sending_date">

                        <button
                          type="button"
                          className="sendDateField"
                          onClick={openDateModal}
                          aria-haspopup="dialog"
                        >
                          <span className="sendDateMeta">
                            <span className="sendDateLabel">{t("Envoi")}</span>
                            <span className="sendDateValue">
                              {dateTimeMode === "now"
                                ? `${t("Aujourd'hui")}, ${moment().format('H')}h`
                                : `${cardData.datepicker || ""}${cardData.timepicker ? `, ${parseInt(cardData.timepicker, 10)}h` : ""}`}
                            </span>
                          </span>
                        </button>

                        <SendDateModal
                          isOpen={dateModalOpen}
                          onClose={cancelDateModal}
                          onImmediate={chooseImmediateSend}
                          onConfirm={() => setDateModalOpen(false)}
                        >
                          <div className="dateModalFields">
                            <div className="dateModalFieldGroup">
                              <label htmlFor="sendDateInput">{t("Envoi le")}</label>
                              <input
                                id="sendDateInput"
                                type="text"
                                className="dateModalDateInput"
                                value={cardData.datepicker || moment().format(store.format_date)}
                                readOnly
                              />
                            </div>
                            <div className="dateModalFieldGroup">
                              <label htmlFor="sendTimeSelect">{t("À")}</label>
                              <TimeListbox
                                id="sendTimeSelect"
                                label={t("À")}
                                value={cardData.timepicker || moment().format('HH:00')}
                                onChange={(v) => {
                                  const { time, timepicker } = formatDateTime.handleTimeChange(v, store);
                                  setCardData(prev => ({
                                    ...prev,
                                    time,
                                    timepicker,
                                    sendDate: `${prev.date} ${time}`,
                                    sendDateTmp: Math.floor(new Date(`${prev.date} ${time}`).getTime() / 1000),
                                  }));
                                }}
                              />
                            </div>
                          </div>

                          <DateTimePicker
                            inline
                            value={cardData.datepicker || moment().format(store.format_date)}
                            onChange={(newDate) => {
                              const { date, datepicker } = formatDateTime.handleDateChange(newDate, store);
                              setCardData(prev => ({
                                ...prev,
                                date,
                                datepicker,
                                sendDate: `${date} ${prev.time}`,
                                sendDateTmp: Math.floor(new Date(`${date} ${prev.time}`).getTime() / 1000),
                              }));
                            }}
                            format={store.format_date || 'DD/MM/YYYY'}
                            locale={lang}
                            isDate={true}
                          />
                        </SendDateModal>
                      </div>
                    </div>
                  )}

                  {hasAmount && modeStepDone && store.languages && store.languages.length > 1 ?
                    <EmailLanguageSelector
                      options={store.languages}
                      displayFlag={store.display_language_flag}
                      defaultLanguage={store.default_language}
                      onLanguageChange={handleLanguageChange}
                    />
                    :
                    null}

                  {hasAmount && modeStepDone && (
                  <div className={`destSection${emailLangChosen ? "" : " zoneDisabled"}`}>
                  {destinationType === 'email' && senderBlock}

                  {!isProSignboard && (
                    <h2 className="rfTitle" style={{ padding: 0, textAlign: 'left', marginTop: 32 }}>
                      {t("Pour qui est-ce ?")}
                    </h2>
                  )}

                  {!isProSignboard && destinationType === 'email' && (
                    <div className="formControl customCheckbox selfCheck">
                      <div className="optionItem checkboxRadio">
                        <input
                          type="checkbox"
                          name="isForSelf"
                          id="isForSelf"
                          checked={!!cardData.isForSelf}
                          onChange={(e) => setCardData(prev => ({ ...prev, isForSelf: e.target.checked }))}
                        />
                        <label htmlFor="isForSelf">{t("C'est pour moi-même")}</label>
                      </div>
                    </div>
                  )}

                  {/* Champs de coordonnées */}
                  <div className="fieldsGroup">
                    {!isProSignboard && !cardData.isForSelf && (
                      <>
                        <div className="formField" id="row_desfirstname">
                          <div className="formControl">
                            <input
                              type="text"
                              name="desfirstname"
                              id="desfirstname"
                              placeholder={t("Prénom")}
                              aria-label={t("Prénom")}
                              required
                              aria-required="true"
                              value={cardData.desfirstname || ""}
                              onChange={(e) =>
                                setCardData({ ...cardData, desfirstname: e.target.value })
                              }
                              className={errors.desfirstname ? 'error' : ''}
                              aria-invalid={errors.desfirstname ? 'true' : 'false'}
                              aria-describedby={errors.desfirstname ? 'error-desfirstname' : undefined}
                            />
                            <label className="flLabel" htmlFor="desfirstname" aria-hidden="true">{t("Prénom")}</label>
                          </div>
                          {errors.desfirstname && <FieldError error={errors.desfirstname} id="desfirstname" />}
                        </div>
                        <div className="formField" id="row_deslastname">
                          <div className="formControl">
                            <input
                              type="text"
                              name="deslastname"
                              id="deslastname"
                              placeholder={t("Nom")}
                              aria-label={t("Nom")}
                              required
                              aria-required="true"
                              value={cardData.deslastname || ""}
                              onChange={(e) =>
                                setCardData({ ...cardData, deslastname: e.target.value })
                              }
                              className={errors.deslastname ? 'error' : ''}
                              aria-invalid={errors.deslastname ? 'true' : 'false'}
                              aria-describedby={errors.deslastname ? 'error-deslastname' : undefined}
                            />
                            <label className="flLabel" htmlFor="deslastname" aria-hidden="true">{t("Nom")}</label>
                            {errors.deslastname && <FieldError error={errors.deslastname} id="deslastname" />}
                          </div>
                        </div>
                        {destinationType === "email" && (
                          <>
                            <div className="formField" id="row_desemail">
                              <div className="formControl">
                                <input
                                  type="email"
                                  name="desemail"
                                  id="desemail"
                                  placeholder={t("Adresse email du destinataire")}
                                  aria-label={t("Adresse email du destinataire")}
                                  required
                                  aria-required="true"
                                  value={cardData.desemail || ""}
                                  onChange={(e) =>
                                    setCardData({ ...cardData, desemail: e.target.value })
                                  }
                                  className={errors.desemail ? 'error' : ''}
                                  aria-invalid={errors.desemail ? 'true' : 'false'}
                                  aria-describedby={errors.desemail ? 'error-desemail' : undefined}
                                />
                                <label className="flLabel" htmlFor="desemail" aria-hidden="true">{t("Adresse email du destinataire")}</label>
                              </div>
                              <p className="fieldNote">
                                {t("Veuillez privilégier une adresse personnelle car les serveurs professionnels peuvent filtrer les informations commerciales")}
                              </p>
                              {errors.desemail && <FieldError error={errors.desemail} id="desemail" />}
                            </div>
                            <div className="formField" id="row_desconfEmail">
                              <div className="formControl">
                                <input
                                  type="email"
                                  name="desconfEmail"
                                  id="desconfEmail"
                                  required
                                  aria-required="true"
                                  placeholder={t("Email confirmation")}
                                  aria-label={t("Email confirmation")}
                                  value={cardData.desconfEmail || ""}
                                  onChange={(e) =>
                                    setCardData({ ...cardData, desconfEmail: e.target.value })
                                  }
                                  className={errors.desconfEmail ? 'error' : ''}
                                  aria-invalid={errors.desconfEmail ? 'true' : 'false'}
                                  aria-describedby={errors.desconfEmail ? 'error-desconfEmail' : undefined}
                                />
                                <label className="flLabel" htmlFor="desconfEmail" aria-hidden="true">{t("Email confirmation")}</label>
                                {errors.desconfEmail && <FieldError error={errors.desconfEmail} id="desconfEmail" />}
                              </div>
                            </div>
                          </>
                        )}
                      </>
                    )}
                    {selectedStore === 'physical' && (
                      <>
                        <div className="formField" id="row_des_address_1">
                          <div className="formControl">
                            <input
                              type="text"
                              name="des_address_1"
                              id="des_address_1"
                              placeholder={t("Adresse ligne 1") + "*"}
                              aria-label={t("Adresse ligne 1")}
                              required
                              aria-required="true"
                              value={cardData.des_address_1 || ""}
                              onChange={(e) =>
                                setCardData({ ...cardData, des_address_1: e.target.value })
                              }
                              className={errors.des_address_1 ? 'error' : ''}
                            />
                          </div>
                        </div>
                        <div className="formField" id="row_des_address_2">
                          <div className="formControl">
                            <input
                              type="text"
                              name="des_address_2"
                              id="des_address_2"
                              placeholder={t("Complément adresse ligne 2")}
                              aria-label={t("Complément adresse ligne 2")}
                              value={cardData.des_address_2 || ""}
                              onChange={(e) =>
                                setCardData({ ...cardData, des_address_2: e.target.value })
                              }
                            />
                          </div>
                        </div>
                        {nbAddressLine >= 3 && (
                          <div className="formField" id="row_des_address_3">
                            <div className="formControl">
                              <input
                                type="text"
                                name="des_address_3"
                                id="des_address_3"
                                placeholder={t("Complément adresse ligne 3")}
                                aria-label={t("Complément adresse ligne 3")}
                                value={cardData.des_address_3 || ""}
                                onChange={(e) =>
                                  setCardData({ ...cardData, des_address_3: e.target.value })
                                }
                              />
                            </div>
                          </div>
                        )}
                        {nbAddressLine >= 4 && (
                          <div className="formField" id="row_des_address_4">
                            <div className="formControl">
                              <input
                                type="text"
                                name="des_address_4"
                                id="des_address_4"
                                placeholder={t("Complément adresse ligne 4")}
                                aria-label={t("Complément adresse ligne 4")}
                                value={cardData.des_address_4 || ""}
                                onChange={(e) =>
                                  setCardData({ ...cardData, des_address_4: e.target.value })
                                }
                              />
                            </div>
                          </div>
                        )}
                        <div className="formField" id="row_des_city">
                          <div className="formControl">
                            <input
                              type="text"
                              name="des_city"
                              id="des_city"
                              placeholder={t("Ville") + '*'}
                              aria-label={t("Ville")}
                              required
                              aria-required="true"
                              value={cardData.des_city || ""}
                              onChange={(e) =>
                                setCardData({ ...cardData, des_city: e.target.value })
                              }
                              className={errors.des_city ? 'error' : ''}
                            />
                          </div>
                        </div>
                        <div className="formField" id="row_des_zip">
                          <div className="formControl">
                            <input
                              type="text"
                              name="des_zip"
                              id="des_zip"
                              placeholder={t("Code postal") + "*"}
                              aria-label={t("Code postal")}
                              required
                              aria-required="true"
                              value={cardData.des_zip || ""}
                              onChange={(e) =>
                                setCardData({ ...cardData, des_zip: e.target.value })
                              }
                              className={errors.des_zip ? 'error' : ''}
                            />
                          </div>
                        </div>
                        <div className="formField" id="row_des_country">
                          <div className="formControl">
                            <select
                              name="des_country"
                              id="des_country"
                              aria-label={t("Pays")}
                              aria-required="true"
                              value={cardData.des_country || ""}
                              onChange={(e) =>
                                setCardData({ ...cardData, des_country: e.target.value })
                              }
                            >
                              <option value="">{t("Pays") + '*'}</option>
                              {store.physical_store &&
                                store.physical_store.countries &&
                                store.physical_store.countries.map((country, index) => (
                                  <option key={index} value={country[lang]}>
                                    {country[lang]}
                                  </option>
                                ))}
                            </select>
                          </div>
                        </div>
                      </>
                    )}

       
          
   

                    {/* Zone d'affichage quantité */}

                    {orderCardByAmountInCart && selectedStore === 'digital' && (
                      <div >
                        <h2 className="rfTitle" style={{ padding: 0, marginTop: 32 }}>{t("Quantité :")}</h2>
                        <NumberInput id="quantity" min={1} value={quantity} onChange={setQuantity} />
                      </div>
                    )}
                  </div>
                  {destinationType !== 'email' && senderBlock}
                  </div>
                  )}

                  {hasAmount && (() => {
                    const fieldErrorKeys = ['desfirstname', 'deslastname', 'desemail', 'desconfEmail', 'senderfirstname', 'senderlastname', 'senderemail'];
                    const globalErrors = Object.entries(errors).filter(([key]) => !fieldErrorKeys.includes(key));
                    return globalErrors.length > 0 && (
                      <div className="formField">
                        <div className="errorBox" role="alert" aria-live="polite">
                          <div className="error-summary">
                            <span className="error-icon" aria-hidden="true">⚠️</span>
                            <span className="error-title">{t("Veuillez corriger les erreurs suivantes :")}</span>
                            <ul>
                              {globalErrors.map(([key, message]) => (
                                <li key={key}>{message}</li>
                              ))}
                            </ul>
                          </div>
                        </div>
                      </div>
                    );
                  })()}

                </div>

              </div>
              <div className="actionsRow labelMargin">
                {!!isFormValid && (
                  <div className="cartRecap">
                    {cardData.selectedCard?.src && (
                      <img className="recapCardImg" src={cardData.selectedCard.src} alt="" />
                    )}
                    {cardData.montant > 0 && (
                      <div className="recapAmount">
                        {formatAmounts(cardData.montant, cardData.montant % 1 ? 2 : 0)}
                      </div>
                    )}
                    {selectedStore === 'digital' && (
                      <div className="recapBadge">
                        {dateTimeMode === 'after' && cardData.datepicker
                          ? `${t("Envoi le")} ${cardData.datepicker} ${t("À").toLowerCase()} ${parseInt(cardData.timepicker, 10) || 0}h`
                          : t("Envoi immédiat")}
                      </div>
                    )}
                    <div className="recapReceiver">
                      <div className="recapName">{cardData.desfirstname} {cardData.deslastname}</div>
                      {destinationType === 'email' && cardData.desemail && (
                        <div className="recapEmail">{cardData.desemail}</div>
                      )}
                    </div>
                    {cardData.customMsg && (
                      <div className="recapMsg">{cardData.customMsg}</div>
                    )}
                    {selectedStore === 'digital' && displayDemoButton && store.template && store.template[lang] && (
                      <button
                        type="button"
                        className="recapPreviewLink"
                        onClick={showModalDemo}
                      >
                        {t("Visualiser l'email qui sera envoyé")}
                      </button>
                    )}
                  </div>
                )}
                <Button
                  type="submit"
                  name="submit"
                  id="submit"
                  className={isFormValid ? "" : "disabled"}
                  isLoading={isAdding}
                  style={{ maxWidth: "100%" }}
                >
                  {t("Ajouter au panier")}
                </Button>
                <div className="sr-only" role="status" aria-live="polite">{addedFeedback}</div>
                {store.mentionLegale && store.mentionLegale[lang] && (
                  <AccessibleContent
                    htmlContent={store.mentionLegale[lang]}
                    className="bottomSection2 mainColor"
                  />
                )}
              </div>
            </div>

          </div>
        </section>
      </form>
      {showModal && (
        <div role="dialog" aria-modal="true" aria-labelledby="modal-title">
          <Modal
            modalType={modalType}
            onHideModal={() => {
              setShowModal(false);
              setModalType(null);
            }}
            onAutreCarteModal={handleAutreCarteModal}
            onValiderAchat={handleValiderAchat}
            itemToDelete={itemToDelete}
            onDeleteItem={(item) => {
              setShowModal(false);
              setModalType(null);
              deleteRequestCartItem(item)
            }}
            store={store}
            previewUrl={previewUrl}
          />
        </div>
      )}


    </div>
  );
};

export default Step1;